import React, { useEffect, useState, createContext, useContext } from 'react';
import { WindowState, WindowContextValue, WindowPosition, WindowSize } from '../types/window';
const WindowContext = createContext<WindowContextValue | undefined>(undefined);
interface WindowProviderProps {
  children: React.ReactNode;
}
export function WindowProvider({
  children
}: WindowProviderProps) {
  const [windows, setWindows] = useState<WindowState[]>([]);
  const [highestZIndex, setHighestZIndex] = useState(100);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const generateWindowId = () => {
    return `window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };
  const getDefaultPosition = (): WindowPosition => {
    const offset = windows.length * 40;
    return {
      x: Math.min(100 + offset, window.innerWidth - 400),
      y: Math.min(80 + offset, window.innerHeight - 300)
    };
  };
  const getDefaultSize = (): WindowSize => {
    if (isMobile) {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      };
    }
    return {
      width: Math.min(800, window.innerWidth - 200),
      height: Math.min(600, window.innerHeight - 200)
    };
  };
  const openWindow = (appId: string, title: string, projectId?: string) => {
    const existingWindow = windows.find(w => w.appId === appId && (!projectId || w.projectId === projectId));
    if (existingWindow) {
      if (existingWindow.isMinimized) {
        // Restore minimized window
        setWindows(prev => prev.map(w => {
          if (w.id === existingWindow.id) {
            return {
              ...w,
              isMinimized: false
            };
          }
          // On mobile, minimize all other windows
          if (isMobile && w.id !== existingWindow.id) {
            return {
              ...w,
              isMinimized: true
            };
          }
          return w;
        }));
      } else if (isMobile) {
        // On mobile, minimize all others and ensure this one is on top
        setWindows(prev => prev.map(w => {
          if (w.id === existingWindow.id) return w;
          return {
            ...w,
            isMinimized: true
          };
        }));
      }
      focusWindow(existingWindow.id);
      return;
    }
    const newZIndex = highestZIndex + 1;
    setHighestZIndex(newZIndex);
    const newWindow: WindowState = {
      id: generateWindowId(),
      appId,
      title,
      position: getDefaultPosition(),
      size: getDefaultSize(),
      zIndex: newZIndex,
      isMinimized: false,
      isMaximized: isMobile,
      projectId
    };
    // On mobile, minimize all existing windows when opening a new one
    if (isMobile) {
      setWindows(prev => [...prev.map(w => ({
        ...w,
        isMinimized: true
      })), newWindow]);
    } else {
      setWindows(prev => [...prev, newWindow]);
    }
  };
  const closeWindow = (id: string) => {
    setWindows(prev => {
      const remaining = prev.filter(w => w.id !== id);
      // On mobile, restore the most recent remaining window
      if (isMobile && remaining.length > 0) {
        const topWindow = remaining.reduce((a, b) => a.zIndex > b.zIndex ? a : b);
        return remaining.map(w => w.id === topWindow.id ? {
          ...w,
          isMinimized: false
        } : w);
      }
      return remaining;
    });
  };
  const minimizeWindow = (id: string) => {
    setWindows(prev => {
      const updated = prev.map(w => w.id === id ? {
        ...w,
        isMinimized: !w.isMinimized
      } : w);
      // On mobile, if we're restoring a window, minimize all others
      const target = prev.find(w => w.id === id);
      if (isMobile && target?.isMinimized) {
        return updated.map(w => {
          if (w.id === id) return w; // Already toggled above
          return {
            ...w,
            isMinimized: true
          };
        });
      }
      return updated;
    });
  };
  const maximizeWindow = (id: string) => {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      isMaximized: !w.isMaximized,
      position: !w.isMaximized ? {
        x: 0,
        y: 0
      } : w.position
    } : w));
  };
  const focusWindow = (id: string) => {
    const targetWindow = windows.find(w => w.id === id);
    if (!targetWindow) return;
    const maxZIndex = Math.max(...windows.map(w => w.zIndex));
    if (targetWindow.zIndex < maxZIndex) {
      const newZIndex = highestZIndex + 1;
      setHighestZIndex(newZIndex);
      setWindows(prev => prev.map(w => w.id === id ? {
        ...w,
        zIndex: newZIndex
      } : w));
    }
  };
  const updateWindowPosition = (id: string, position: WindowPosition) => {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      position
    } : w));
  };
  const updateWindowSize = (id: string, {
    newWidth,
    newHeight,
    direction
  }: {
    newWidth: number;
    newHeight: number;
    direction: string;
  }) => {
    setWindows(prev => prev.map(w => {
      if (w.id !== id) return w;
      const MIN_WIDTH = 200;
      const MIN_HEIGHT = 100;
      let width = w.size.width;
      let height = w.size.height;
      let newX = w.position.x;
      let newY = w.position.y;
      if (direction.includes('s')) {
        height = Math.max(newHeight, MIN_HEIGHT);
      }
      if (direction.includes('n')) {
        const delta = w.size.height - Math.max(newHeight, MIN_HEIGHT);
        height = w.size.height - delta;
        newY = w.position.y + delta;
      }
      if (direction.includes('e')) {
        width = Math.max(newWidth, MIN_WIDTH);
      }
      if (direction.includes('w')) {
        const delta = w.size.width - Math.max(newWidth, MIN_WIDTH);
        width = w.size.width - delta;
        newX = w.position.x + delta;
      }
      width = Math.max(width, MIN_WIDTH);
      height = Math.max(height, MIN_HEIGHT);
      return {
        ...w,
        position: {
          x: newX,
          y: newY
        },
        size: {
          width,
          height
        }
      };
    }));
  };
  const closeAllWindows = () => {
    setWindows([]);
  };
  const value: WindowContextValue = {
    windows,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    focusWindow,
    updateWindowPosition,
    updateWindowSize,
    closeAllWindows,
    isMobile
  };
  return <WindowContext.Provider value={value} data-id="element-261" data-mp="2dl6ysh2t2ndm">{children}</WindowContext.Provider>;
}
export function useWindows() {
  const context = useContext(WindowContext);
  if (!context) {
    throw new Error('useWindows must be used within WindowProvider');
  }
  return context;
}